import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Container,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ProjectMembersList from "../../../components/ProjectShare/ProjectMembersList";
import {
  shareProjectRequest,
  generateShareLinkRequest,
  fetchProjectDetailRequest,
  fetchUserPermissionRequest,
  fetchProjectSharesRequest,
} from "../../../redux/projects/projectsSlice";
import styles from "./ProjectSettingsPage.module.css";
import default_theme from "assets/themes/themes";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    "& fieldset": { borderColor: "rgba(35, 87, 107, 0.15)" },
    "&:hover fieldset": { borderColor: "#35B3AB" },
    "&.Mui-focused fieldset": { borderColor: "#35B3AB", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { color: "#7898A4" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#35B3AB" },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    "& fieldset": { borderColor: "rgba(35, 87, 107, 0.15)" },
    "&:hover fieldset": { borderColor: "#35B3AB" },
    "&.Mui-focused fieldset": { borderColor: "#35B3AB", borderWidth: 2 },
  },
};

const ProjectSettingsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    currentProjectDetail,
    detailLoading,
    detailError,
    shareLink,
    shareLinkLoading,
    sharesError,
    sharesLoading,
    userPermission,
  } = useSelector((state) => state.projects);

  const authState = useSelector((state) => state.auth);
  const currentUserId = authState?.user?.id;

  const [email, setEmail] = useState("");
  const [emailPermission, setEmailPermission] = useState("viewer");
  const [linkPermission, setLinkPermission] = useState("viewer");
  const [copied, setCopied] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [tempError, setTempError] = useState("");

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectDetailRequest({ projectId }));
      dispatch(fetchUserPermissionRequest({ projectId }));
    }
  }, [projectId, dispatch]);

  const prevSharesLoading = useRef(sharesLoading);

  useEffect(() => {
    if (prevSharesLoading.current && !sharesLoading && !sharesError && email.trim()) {
      setSuccessMessage("Invitation sent successfully!");
      setEmailError("");
      setTempError("");
      setEmail("");
      dispatch(fetchProjectSharesRequest({ projectId }));
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
      prevSharesLoading.current = sharesLoading;
      return () => clearTimeout(timer);
    }

    if (sharesError) {
      setEmailError(sharesError);
      setTempError("");
      setSuccessMessage("");
    } else {
      if (!sharesLoading) {
        setEmailError("");
        setTempError("");
      }
    }

    prevSharesLoading.current = sharesLoading;
  }, [sharesError, sharesLoading, email, projectId, dispatch]);

  const isOwner = currentProjectDetail && currentProjectDetail.owner_id &&
    String(currentProjectDetail.owner_id) === String(currentUserId);

  const handleBackClick = () => {
    navigate(`/workspaces/projects/${projectId}`);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError("");
    setTempError("");
    setSuccessMessage("");
  };

  const handleShareByEmail = () => {
    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError("");
    setTempError("");
    setSuccessMessage("");

    dispatch(
      shareProjectRequest({
        projectId,
        email: email.trim(),
        permission: emailPermission,
      })
    );
  };

  const handleGenerateLink = () => {
    dispatch(
      generateShareLinkRequest({
        projectId,
        permission: linkPermission,
      })
    );
  };

  const handleCopyLink = () => {
    if (shareLink?.share_url) {
      navigator.clipboard.writeText(shareLink.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (detailLoading && !currentProjectDetail) {
    return (
      <Box className={styles.pageContainer}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
          <CircularProgress sx={{ color: "#35B3AB" }} />
        </Box>
      </Box>
    );
  }

  if (detailError) {
    return (
      <Box className={styles.pageContainer}>
        <Container maxWidth="sm" className={styles.container}>
          <Alert severity="error" sx={{ borderRadius: "12px" }}>{detailError}</Alert>
          <Button onClick={handleBackClick} sx={{ mt: 2 }} className={styles.backButton}>
            Back to Project
          </Button>
        </Container>
      </Box>
    );
  }

  if (!isOwner) {
    return (
      <Box className={styles.pageContainer}>
        <Container maxWidth="sm" className={styles.container}>
          <Alert severity="warning" sx={{ borderRadius: "12px" }}>
            You don't have permission to access project settings. Only the project owner can manage settings.
          </Alert>
          <Button onClick={handleBackClick} sx={{ mt: 2 }} className={styles.backButton}>
            Back to Project
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box className={styles.pageContainer}>
      <Container maxWidth="sm" className={styles.container}>
        {/* Header */}
        <Box className={styles.header}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            className={styles.backButton}
          >
            Back to Project
          </Button>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, color: "#23576B", mb: 0.5 }}>
          Project Settings
        </Typography>
        <Typography variant="body1" sx={{ color: "#7898A4", mb: 4, lineHeight: 1.7 }}>
          Manage who can access this project and how they interact with it.
        </Typography>

        {/* Share Section */}
        <Box className={styles.dataPanel} sx={{ mb: 4 }}>
          <Box className={styles.sectionLabel}>
            <Box className={styles.sectionDot} />
            <Typography variant="h6" className={styles.sectionTitle}>
              Share Project
            </Typography>
          </Box>

          {/* ── Email Invitation (Coming Soon) ── */}
          <Box sx={{ mb: 4, opacity: 0.6, pointerEvents: "none" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography className={styles.sectionHeading} sx={{ mb: 0 }}>
                Email Invitation
              </Typography>
              <Box sx={{
                px: 1,
                py: 0.25,
                borderRadius: "6px",
                backgroundColor: "rgba(53, 179, 171, 0.1)",
                border: "1px solid rgba(53, 179, 171, 0.2)",
              }}>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#35B3AB", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Coming Soon
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" className={styles.sectionText}>
              Invite someone to collaborate on this project by entering their email address.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                disabled
                sx={textFieldSx}
              />
              <FormControl sx={{ minWidth: 200 }} sx={selectSx}>
                <InputLabel sx={{ color: "#7898A4" }}>Permission</InputLabel>
                <Select
                  value="viewer"
                  label="Permission"
                  disabled
                  sx={{ borderRadius: "12px", color: "#23576B" }}
                >
                  <MenuItem value="viewer">Viewer — Can view project</MenuItem>
                  <MenuItem value="editor">Editor — Can view and edit project</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled
                sx={{
                  background: "linear-gradient(135deg, #35B3AB, #23576B)",
                  color: "#fff",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "12px",
                  px: 3,
                  minWidth: 180,
                }}
              >
                Send Invitation
              </Button>
            </Box>
          </Box>

          {/*
          {/* ── Email Invitation (Active) ── * /}
          <Box sx={{ mb: 4 }}>
            <Typography className={styles.sectionHeading}>
              Email Invitation
            </Typography>
            <Typography variant="body2" className={styles.sectionText}>
              Invite someone to collaborate on this project by entering their email address.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                error={!!emailError}
                helperText={emailError}
                sx={textFieldSx}
              />
              <FormControl sx={{ minWidth: 200 }} sx={selectSx}>
                <InputLabel sx={{ color: "#7898A4" }}>Permission</InputLabel>
                <Select
                  value={emailPermission}
                  label="Permission"
                  onChange={(e) => setEmailPermission(e.target.value)}
                  sx={{ borderRadius: "12px", color: "#23576B" }}
                >
                  <MenuItem value="viewer">Viewer — Can view project</MenuItem>
                  <MenuItem value="editor">Editor — Can view and edit project</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleShareByEmail}
                disabled={!email.trim()}
                sx={{
                  background: "linear-gradient(135deg, #35B3AB, #23576B)",
                  color: "#fff",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "12px",
                  px: 3,
                  minWidth: 180,
                  boxShadow: "0 2px 8px rgba(53, 179, 171, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #2fa39b, #1B4454)",
                    boxShadow: "0 4px 12px rgba(53, 179, 171, 0.4)",
                  },
                  "&:disabled": {
                    background: "rgba(35, 87, 107, 0.12)",
                    color: "rgba(35, 87, 107, 0.38)",
                    boxShadow: "none",
                  },
                }}
              >
                Send Invitation
              </Button>
            </Box>
            {successMessage && (
              <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon sx={{ color: "#35B3AB" }} />}
                sx={{ mt: 2, borderRadius: "12px", border: "1px solid rgba(53, 179, 171, 0.2)" }}
              >
                {successMessage}
              </Alert>
            )}
            {emailError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: "12px" }}>
                {emailError}
              </Alert>
            )}
          </Box>
          */}

          <Divider className={styles.divider} sx={{ my: 3 }} />

          {/* Shareable Link */}
          <Box>
            <Typography className={styles.sectionHeading}>
              Shareable Link
            </Typography>
            <Typography variant="body2" className={styles.sectionText}>
              Generate a shareable link that anyone can use to join this project.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
              <FormControl sx={{ minWidth: 200 }} sx={selectSx}>
                <InputLabel sx={{ color: "#7898A4" }}>Default Permission</InputLabel>
                <Select
                  value={linkPermission}
                  label="Default Permission"
                  onChange={(e) => setLinkPermission(e.target.value)}
                  sx={{ borderRadius: "12px", color: "#23576B" }}
                >
                  <MenuItem value="viewer">Viewer — Can view project</MenuItem>
                  <MenuItem value="editor">Editor — Can view and edit project</MenuItem>
                </Select>
              </FormControl>
              {!shareLink && (
                <Button
                  variant="outlined"
                  onClick={handleGenerateLink}
                  disabled={shareLinkLoading}
                  sx={{
                    borderColor: "#35B3AB",
                    color: "#35B3AB",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "12px",
                    minWidth: 200,
                    "&:hover": {
                      borderColor: "#23576B",
                      color: "#fff",
                      backgroundColor: "#23576B",
                    },
                    "&:disabled": {
                      borderColor: "rgba(35, 87, 107, 0.15)",
                      color: "rgba(35, 87, 107, 0.38)",
                    },
                  }}
                >
                  {shareLinkLoading ? <CircularProgress size={24} color="inherit" /> : "Generate Share Link"}
                </Button>
              )}
            </Box>
            {shareLink && (
              <Box sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px solid rgba(35, 87, 107, 0.1)",
                backgroundColor: "rgba(53, 179, 171, 0.03)",
              }}>
                <TextField
                  fullWidth
                  value={shareLink.share_url}
                  InputProps={{
                    readOnly: true,
                    sx: { borderRadius: "10px", fontSize: "0.875rem" },
                    endAdornment: (
                      <IconButton
                        onClick={handleCopyLink}
                        edge="end"
                        sx={{
                          color: "#35B3AB",
                          "&:hover": { backgroundColor: "rgba(53, 179, 171, 0.1)" },
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />
                {copied && (
                  <Alert
                    severity="success"
                    icon={<CheckCircleOutlineIcon sx={{ color: "#35B3AB" }} />}
                    sx={{ mb: 1, borderRadius: "8px", border: "1px solid rgba(53, 179, 171, 0.2)" }}
                  >
                    Link copied to clipboard!
                  </Alert>
                )}
                <Typography variant="caption" sx={{ color: "#7898A4" }}>
                  Default permission: {shareLink.permission === "viewer" ? "Viewer" : "Editor"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Project Members Section */}
        <Box className={styles.dataPanel}>
          <Box className={styles.sectionLabel}>
            <Box className={styles.sectionDot} />
            <Typography variant="h6" className={styles.sectionTitle}>
              Project Members
            </Typography>
          </Box>
          <ProjectMembersList
            projectId={projectId}
            isOwner={isOwner}
            ownerId={currentProjectDetail?.owner_id}
            currentUserId={currentUserId}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default ProjectSettingsPage;
